<script setup lang="ts">
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { AppSelect } from '@shared/ui/selects'
import { CardAdmin } from '@shared/ui/cards'
import { BtnPrimary } from '@shared/ui/buttons'

import { useHelperAction } from '../../api/helpers/action.hook'
import { useDemoConversionRewardMonthsApi } from '../../api/helpers/demo-conversion-reward-months.hook'

defineOptions({
  name: 'HelperTcrBonusCard',
})

const { run, isRunning, canRun } = useHelperAction()

const refMonthOptions = ref<AppSelectOption<string>[]>([])
const refMonth = ref<string>()
const refIsLoadingMonths = ref<boolean>(false)
const refHasMonthsLoadFailed = ref<boolean>(false)

const hasMonths = computed<boolean>(() => refMonthOptions.value.length > 0)
const isMonthSelectDisabled = computed<boolean>(() => refIsLoadingMonths.value || !hasMonths.value || isRunning.value)
const canSend = computed<boolean>(() => canRun.value && !isRunning.value && Boolean(refMonth.value))

const monthPlaceholder = computed<string>(() => {
  if (refIsLoadingMonths.value) return 'Loading months…'
  if (refHasMonthsLoadFailed.value) return 'Could not load months'
  if (!hasMonths.value) return 'No closed months yet'

  return 'Select month'
})

const loadMonths = async (): Promise<void> => {
  const options = await useDemoConversionRewardMonthsApi({
    onLoadingChange: (loading) => {
      refIsLoadingMonths.value = loading
    },
  })

  refHasMonthsLoadFailed.value = options === null
  refMonthOptions.value = options ?? []
  refMonth.value = refMonthOptions.value[0]?.value
}

const send = async (): Promise<void> => {
  if (!refMonth.value) return

  await run('admin.helpers.tutor.demo-conversion-rewards.create', { month: refMonth.value })
}

onMounted(() => {
  void loadMonths()
})
</script>

<template>
  <CardAdmin
    title="Calculate TCR bonus for month"
    subtitle="Creates the demo conversion bonus (Performance reward) for every tutor without one for the chosen closed month."
    fullwidth
  >
    <div class="helper-tcr-bonus">
      <AppSelect
        v-model="refMonth"
        class="helper-tcr-bonus__month"
        :options="refMonthOptions"
        label="Month"
        :placeholder="monthPlaceholder"
        :disabled="isMonthSelectDisabled"
      />
      <BtnPrimary
        :text="isRunning ? 'creating…' : 'create'"
        :disabled="!canSend"
        @click="send"
      />
    </div>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./HelperTcrBonusCard.scss" />
